import React, {createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface BasketLine {
  product_handle: string;
  variant_id: string;
  title: string;
  variant_title: string;
  image: string | null;
  unit_price_pence: number;
  quantity: number;
}

interface BasketCtx {
  lines: BasketLine[];
  add: (line: Omit<BasketLine, "quantity">, qty?: number) => void;
  setQty: (variant_id: string, qty: number) => void;
  remove: (variant_id: string) => void;
  clear: () => void;
  total: number;
  count: number;
  open: boolean;
  setOpen: (o: boolean) => void;
}

const Ctx = createContext<BasketCtx>(null as any);

export function BasketProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<BasketLine[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("cellar_basket") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("cellar_basket", JSON.stringify(lines));
  }, [lines]);

  const add = useCallback((line: Omit<BasketLine, "quantity">, qty = 1) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.variant_id === line.variant_id);
      if (existing) return prev.map((l) => (l.variant_id === line.variant_id ? { ...l, quantity: l.quantity + qty } : l));
      return [...prev, { ...line, quantity: qty }];
    });
    setOpen(true);
  }, []);
  const setQty = useCallback((variant_id: string, qty: number) =>
    setLines((prev) => (qty <= 0 ? prev.filter((l) => l.variant_id !== variant_id) : prev.map((l) => (l.variant_id === variant_id ? { ...l, quantity: qty } : l)))), []);
  const remove = useCallback((variant_id: string) => setLines((prev) => prev.filter((l) => l.variant_id !== variant_id)), []);
  const clear = useCallback(() => setLines([]), []);

  const total = useMemo(() => lines.reduce((s, l) => s + l.unit_price_pence * l.quantity, 0), [lines]);
  const count = useMemo(() => lines.reduce((s, l) => s + l.quantity, 0), [lines]);

  const value = useMemo(() => ({ lines, add, setQty, remove, clear, total, count, open, setOpen }), [lines, add, setQty, remove, clear, total, count, open]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useBasket = () => useContext(Ctx);
